import { forwardRef, useEffect } from "react";
import type { ReactNode } from "react";
import type { ErrorCode } from "../lib/http/errors";
import { useDensity } from "../store/density";
import { FE_META, feText } from "../i18n/fe";
import type { Audience, Severity } from "../i18n/fe";
import { useT } from "../i18n";
import { track } from "../lib/telemetry/telemetry";
import { Button } from "./ui/Button";
import { ReadAloudButton } from "./ReadAloud";

export interface BannerAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "primary" | "ghost";
}

export interface FeBannerProps {
  /** FE-xxx(또는 셸 NET-*) — 문구는 카탈로그(fe.<code>.<audience>)에서 고른다 */
  code: ErrorCode;
  /** 학생: 피코 어휘·코드 숨김 / 교사·운영: 원인 · 다음 행동 · 문의 코드 */
  audience: Audience;
  /** 기본값은 코드별 FE_META */
  severity?: Severity;
  actions?: readonly BannerAction[];
  /** 부가 내용(지원 브라우저 칩 등) */
  children?: ReactNode;
  /** 읽어 주기 버튼 — 기본: SE 학생만 */
  readAloud?: boolean;
  id?: string;
  /** 학생 배너 앞 아이콘(기본: 코드별 FE_META.icon → 심각도별) — 예: FS-02-02 실패 '✖' */
  icon?: string;
  /**
   * 학생 배너에 **교사용 코드 접힘**을 붙인다(SCR v0.6 FS-02-02 "실패: 피코 문구 + (교사용 코드 접힘)").
   * 접힌 채로 그려지고, 펼치면 '교사: FE-xxx · 부품/관절' 이 보인다. 문자열이면 코드 뒤에 붙는 상세(예: "/ports/B").
   */
  teacherFold?: boolean | string;
}

const ICON: Record<Severity, string> = { err: "🔒", warn: "💡", info: "🚪", ok: "✅" };

/**
 * FS-C1 공통 상태·오류 배너 — 3요소(원인 · 다음 행동 · 문의 코드). SCR v0.6:
 *  - "학생 배너는 코드 없이 다음 행동만" — 학생에게는 코드를 보이지 않는다(data-fe-code 로만 남김).
 *  - 교사·운영은 코드 병기. 누락은 디자인 QA 반려.
 *  - err 는 role=alert(즉시 읽음), 나머지는 role=status(aria-live=polite).
 *  - 점수·순위·비교·탓하는 문구 금지(DR-01) — 문구 카탈로그 검사로 강제.
 */
export const FeBanner = forwardRef<HTMLDivElement, FeBannerProps>(function FeBanner(
  { code, audience, severity, actions, children, readAloud, id, icon, teacherFold },
  ref,
) {
  const t = useT();
  const { density } = useDensity();
  // S5 계측 — 보인 FE 코드(가명 · 코드 키만)
  useEffect(() => track({ t: "fe", code, detail: audience }), [code, audience]);
  const meta = FE_META[code];
  if (audience === "student" && meta.studentSilent) return null;
  const sev = severity ?? meta.severity;
  const { cause, next } = feText(t, code, audience);
  const showRead = readAloud ?? (audience === "student" && density === "se");
  const spoken = [cause, next].filter(Boolean).join(" ");

  return (
    <div
      ref={ref}
      id={id}
      tabIndex={-1}
      className={`pf-banner pf-banner--${sev}`}
      role={sev === "err" ? "alert" : "status"}
      aria-live={sev === "err" ? "assertive" : "polite"}
      aria-atomic="true"
      data-fe-code={code}
      data-audience={audience}
      data-read-aloud={showRead ? "" : undefined}
    >
      <div className="pf-banner__body">
        {audience === "student" && (
          <span className="pf-banner__icon" aria-hidden="true">
            {icon ?? meta.icon ?? ICON[sev]}
          </span>
        )}
        <div className="pf-banner__text">
          <b className="pf-banner__cause">{cause}</b>
          {next && <span className="pf-banner__next"> {next}</span>}
          {audience !== "student" && (
            <span className="pf-banner__code"> ({t("common.codeLabel", { code })})</span>
          )}
          {audience === "student" && teacherFold && (
            <details className="pf-banner__fold" data-testid="teacher-fold">
              <summary>{t("common.teacherFold")}</summary>
              <span className="pf-banner__code">
                {t("common.teacherCode", {
                  code: typeof teacherFold === "string" ? `${code} · ${teacherFold}` : code,
                })}
              </span>
            </details>
          )}
        </div>
        {showRead && <ReadAloudButton text={spoken} force />}
      </div>
      {children}
      {actions && actions.length > 0 && (
        <div className="pf-banner__actions">
          {actions.map((a) => (
            <Button key={a.label} variant={a.variant ?? "ghost"} icon={a.icon} onClick={a.onClick}>
              {a.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
});
